import { useEffect, useMemo, useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import {
  MEDIA_ITEM_KINDS,
  formatInteger,
  mediaItemKindLabels,
  type ExternalSourceInfo,
  type MediaItemKind,
  type MediaItemRecord,
} from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import {
  Banner,
  Button,
  Dialog,
  Icon,
  IconButton,
  Select,
  TextField,
  useToast,
} from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { deleteMedia, orderMedia, updateMedia, uploadMedia, type MediaOwner } from '../api';
import { CardHead, DashedButton } from '../bits';
import { PICTURE_SIZES, applyOrder, moveItem, pictureOf, sortGallery, stepIndex, withCover } from './gallery';
import styles from './MediaGallery.module.css';

const ACCEPT = 'image/jpeg,image/png';

export interface MediaGalleryProps {
  owner: MediaOwner;
  items: MediaItemRecord[];
  canEdit: boolean;
  external: ExternalSourceInfo;
  /** Accessible id of the card's eyebrow. */
  headingId: string;
  /** Refetch the record after a change. */
  onChanged: () => void;
}

function countLabel(count: number): string {
  return `${formatInteger(count)} ${count === 1 ? 'Bild' : 'Bilder'}`;
}

/**
 * „Bilder" of the property and unit records (no design screen, D-187): thumbnail grid in gallery order with
 * the „Titelbild" marker, lightbox with arrow keys, JPEG/PNG upload, cover, caption, kind, reorder through
 * the arrow buttons of each tile (keyboard operable) and removal. Imported items cannot be removed while
 * the TeamProQ connection is import-only.
 */
export function MediaGallery({ owner, items, canEdit, external, headingId, onChanged }: MediaGalleryProps) {
  const { toast } = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [local, setLocal] = useState<MediaItemRecord[]>(() => sortGallery(items));
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [uploading, setUploading] = useState<{ done: number; total: number; fraction: number } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => setLocal(sortGallery(items)), [items]);

  const importOnly = external !== null && external.mode === 'import_only';
  const canRemove = (item: MediaItemRecord) => canEdit && !(item.imported && importOnly);

  async function upload(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (files.length === 0) return;
    let failed = 0;
    for (const [index, file] of files.entries()) {
      setUploading({ done: index, total: files.length, fraction: 0 });
      try {
        await uploadMedia(owner, file, 'photo', {
          onProgress: (fraction) => setUploading({ done: index, total: files.length, fraction }),
        });
      } catch (error) {
        failed += 1;
        toast({ message: `${file.name}: ${errorMessage(error)}` });
      }
    }
    setUploading(null);
    const uploaded = files.length - failed;
    if (uploaded > 0) toast({ message: `${countLabel(uploaded)} hochgeladen. Die Dateien werden geprüft.` });
    onChanged();
  }

  async function move(id: string, step: -1 | 1) {
    const ids = moveItem(local, id, step);
    const previous = local;
    setLocal(applyOrder(local, ids));
    try {
      await orderMedia(owner, ids);
      onChanged();
    } catch (error) {
      setLocal(previous);
      toast({ message: errorMessage(error) });
    }
  }

  function tileKey(event: KeyboardEvent<HTMLDivElement>, id: string) {
    if (!canEdit || !event.altKey) return;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      void move(id, -1);
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      void move(id, 1);
    }
  }

  async function setCover(id: string) {
    const previous = local;
    setLocal(withCover(local, id));
    setBusy(true);
    try {
      await updateMedia(id, { isCover: true });
      toast({ message: 'Titelbild gesetzt.' });
      onChanged();
    } catch (error) {
      setLocal(previous);
      toast({ message: errorMessage(error) });
    } finally {
      setBusy(false);
    }
  }

  const progress = uploading
    ? `${formatInteger(uploading.done + 1)} von ${formatInteger(uploading.total)} · ${Math.round(uploading.fraction * 100)} %`
    : null;

  return (
    <section className={styles.card} aria-labelledby={headingId}>
      <CardHead eyebrow="Bilder" id={headingId} end={countLabel(local.length)} />
      {local.length === 0 ? (
        <p className={styles.empty}>
          {canEdit ? 'Noch keine Bilder. Laden Sie Fotos oder Grundrisse hoch.' : 'Noch keine Bilder.'}
        </p>
      ) : (
        <ul className={styles.grid} aria-label="Bilder">
          {local.map((item, index) => (
            <li key={item.id} className={styles.tile}>
              <div className={styles.tileFrame} onKeyDown={(event) => tileKey(event, item.id)}>
                <button
                  type="button"
                  className={styles.thumbButton}
                  onClick={() => setOpenIndex(index)}
                  aria-label={`${item.caption ?? item.name} öffnen`}
                  aria-keyshortcuts={canEdit ? 'Alt+ArrowLeft Alt+ArrowRight' : undefined}
                >
                  <img
                    className={styles.thumb}
                    src={item.thumbUrl}
                    srcSet={pictureOf(item.fileId, { size: item, upTo: 'medium' }).srcSet}
                    sizes={PICTURE_SIZES.galleryTile}
                    alt=""
                    loading="lazy"
                    decoding="async"
                  />
                </button>
                {item.isCover ? (
                  <span className={styles.coverTag}>
                    <Icon icon={Star} size={16} />
                    Titelbild
                  </span>
                ) : item.kind !== 'photo' ? (
                  <span className={styles.kindTag}>{item.kindLabel}</span>
                ) : null}
                {canEdit && local.length > 1 ? (
                  <div className={styles.moveButtons}>
                    <IconButton
                      icon={ChevronLeft}
                      size={28}
                      label={`${item.caption ?? item.name} nach vorne`}
                      disabled={index === 0}
                      onClick={() => void move(item.id, -1)}
                    />
                    <IconButton
                      icon={ChevronRight}
                      size={28}
                      label={`${item.caption ?? item.name} nach hinten`}
                      disabled={index === local.length - 1}
                      onClick={() => void move(item.id, 1)}
                    />
                  </div>
                ) : null}
              </div>
              {item.caption ? <span className={styles.caption}>{item.caption}</span> : null}
            </li>
          ))}
        </ul>
      )}
      {canEdit ? (
        <>
          <input
            ref={input}
            type="file"
            accept={ACCEPT}
            multiple
            hidden
            onChange={(event) => void upload(event)}
          />
          <DashedButton
            className={styles.upload}
            onClick={() => input.current?.click()}
            disabled={uploading !== null}
            aria-busy={uploading !== null || undefined}
          >
            {progress ? `Wird hochgeladen · ${progress}` : 'Bilder hochladen'}
          </DashedButton>
          <p className={styles.hint}>JPEG oder PNG. Die Reihenfolge ändern Sie mit den Pfeilen am Bild.</p>
        </>
      ) : null}
      {openIndex !== null && local.length > 0 ? (
        <Lightbox
          items={local}
          index={Math.min(openIndex, local.length - 1)}
          onIndex={setOpenIndex}
          onClose={() => setOpenIndex(null)}
          canEdit={canEdit}
          canRemove={canRemove}
          busy={busy}
          onCover={(id) => void setCover(id)}
          onSaved={() => onChanged()}
          onRemoved={() => {
            setOpenIndex(null);
            onChanged();
          }}
        />
      ) : null}
    </section>
  );
}

/** The lightbox picture: `large` as fallback, all variants as candidates (picked by stage width and pixel density). */
function stageSource(item: MediaItemRecord) {
  return pictureOf(item.fileId, { size: item, fallback: 'large' });
}

interface LightboxProps {
  items: MediaItemRecord[];
  index: number;
  onIndex: (index: number) => void;
  onClose: () => void;
  canEdit: boolean;
  canRemove: (item: MediaItemRecord) => boolean;
  busy: boolean;
  onCover: (id: string) => void;
  onSaved: () => void;
  onRemoved: () => void;
}

function Lightbox({
  items,
  index,
  onIndex,
  onClose,
  canEdit,
  canRemove,
  busy,
  onCover,
  onSaved,
  onRemoved,
}: LightboxProps) {
  const { toast } = useToast();
  const item = items[index];
  const [caption, setCaption] = useState('');
  const [kind, setKind] = useState<MediaItemKind>('photo');
  const [saving, setSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!item) return;
    setCaption(item.caption ?? '');
    setKind(item.kind);
    setConfirmRemove(false);
    setError(null);
  }, [item]);

  // The neighbours are fetched ahead, so the arrow keys switch without waiting for the network.
  useEffect(() => {
    if (items.length < 2) return;
    for (const step of [-1, 1] as const) {
      const neighbour = items[stepIndex(index, step, items.length)];
      if (!neighbour || neighbour.id === items[index]?.id) continue;
      const preload = new Image();
      preload.sizes = PICTURE_SIZES.lightbox;
      preload.srcset = stageSource(neighbour).srcSet;
      preload.src = neighbour.largeUrl;
    }
  }, [items, index]);

  const kindOptions = useMemo(
    () => MEDIA_ITEM_KINDS.map((value) => ({ value, label: mediaItemKindLabels[value] })),
    [],
  );

  if (!item) return null;
  const current = item;
  const dirty = (caption.trim() || null) !== current.caption || kind !== current.kind;

  function onKey(event: KeyboardEvent<HTMLDivElement>) {
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement) return;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      onIndex(stepIndex(index, -1, items.length));
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      onIndex(stepIndex(index, 1, items.length));
    }
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await updateMedia(current.id, { caption: caption.trim() || null, kind });
      toast({ message: 'Bild gespeichert.' });
      onSaved();
    } catch (failure) {
      setError(errorMessage(failure));
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    setSaving(true);
    setError(null);
    try {
      await deleteMedia(current.id);
      toast({ message: 'Bild entfernt.' });
      onRemoved();
    } catch (failure) {
      setError(errorMessage(failure));
      setSaving(false);
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      width={960}
      eyebrow={`${current.kindLabel} ${formatInteger(index + 1)} von ${formatInteger(items.length)}`}
      title={current.caption ?? current.name}
      footer={
        canEdit ? (
          <>
            {canRemove(current) ? (
              confirmRemove ? (
                <Button variant="destructive" loading={saving} onClick={() => void remove()}>
                  Endgültig entfernen
                </Button>
              ) : (
                <Button variant="ghost" onClick={() => setConfirmRemove(true)} disabled={saving}>
                  Entfernen
                </Button>
              )
            ) : null}
            <Button
              variant="secondary"
              onClick={() => onCover(current.id)}
              disabled={current.isCover || busy || saving}
            >
              {current.isCover ? 'Ist Titelbild' : 'Als Titelbild'}
            </Button>
            <Button loading={saving && !confirmRemove} disabled={!dirty} onClick={() => void save()}>
              Speichern
            </Button>
          </>
        ) : (
          <Button variant="secondary" onClick={onClose}>
            Schließen
          </Button>
        )
      }
    >
      <div className={styles.lightbox} onKeyDown={onKey}>
        {error ? <Banner tone="danger">{error}</Banner> : null}
        <div className={styles.stage}>
          <img
            className={styles.stageImage}
            src={current.largeUrl}
            srcSet={stageSource(current).srcSet}
            sizes={PICTURE_SIZES.lightbox}
            alt={current.caption ?? current.name}
          />
          {items.length > 1 ? (
            <>
              <IconButton
                icon={ChevronLeft}
                label="Vorheriges Bild"
                className={clsx(styles.stageNav, styles.stagePrev)}
                onClick={() => onIndex(stepIndex(index, -1, items.length))}
              />
              <IconButton
                icon={ChevronRight}
                label="Nächstes Bild"
                className={clsx(styles.stageNav, styles.stageNext)}
                onClick={() => onIndex(stepIndex(index, 1, items.length))}
              />
            </>
          ) : null}
        </div>
        {canEdit ? (
          <div className={styles.editRow}>
            <TextField
              label="Bildunterschrift"
              optional
              value={caption}
              maxLength={300}
              onChange={(event) => setCaption(event.target.value)}
            />
            <Select
              label="Art"
              value={kind}
              options={kindOptions}
              onChange={(event) => setKind(event.target.value as MediaItemKind)}
            />
          </div>
        ) : null}
        {confirmRemove ? (
          <p className={styles.confirmText}>
            Das Bild wird aus der Akte entfernt. Diese Aktion lässt sich nicht rückgängig machen.
          </p>
        ) : null}
        {current.imported ? <p className={styles.hint}>Aus TeamProQ übernommen.</p> : null}
      </div>
    </Dialog>
  );
}
